/**
 * Firebase Analytics + Crashlytics 통합 모듈
 *
 * 네이티브 앱(Android/iOS)에서만 활성화됩니다.
 * 로컬 개발 서버(웹 브라우저)에서는 비활성화됩니다.
 * dev/prod 환경은 각 플랫폼의 설정 파일로 자동 분기됩니다:
 * - Android: src/dev/, src/prod/ 의 google-services.json
 * - iOS: GoogleService-Info.plist (빌드 전 스크립트로 교체)
 */
import { Capacitor } from "@capacitor/core";
import { FirebaseAnalytics } from "@capacitor-firebase/analytics";
import { FirebaseCrashlytics } from "@capacitor-firebase/crashlytics";
import { createLogger } from "@ict/shared";

const logger = createLogger({ module: "Firebase" });

// 주요 이벤트 상수
export const AnalyticsEvents = {
  LOGIN: "login",
  SIGN_UP: "sign_up",
  INSPECTION_START: "inspection_start",
  INSPECTION_COMPLETE: "inspection_complete",
  PHOTO_UPLOAD: "photo_upload",
  OFFLINE_SYNC: "offline_sync",
} as const;

type AnalyticsEventName = (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents] | string;

// 네이티브 앱에서만 활성화 (로컬 개발 서버에서는 비활성화)
const isEnabled = Capacitor.isNativePlatform();

/**
 * Firebase 초기화
 * 프로덕션 환경에서만 Analytics 수집을 활성화합니다.
 */
export async function initFirebase(): Promise<void> {
  if (!isEnabled) {
    logger.debug("개발 환경 - Firebase 비활성화");
    return;
  }

  try {
    await FirebaseAnalytics.setEnabled({ enabled: true });
    await FirebaseCrashlytics.setEnabled({ enabled: true });
    logger.info("Firebase Analytics + Crashlytics 초기화 완료");
  } catch (error) {
    // 웹 브라우저(PWA)에서는 네이티브 플러그인 미지원 - 무시
    logger.warn("Firebase 초기화 실패 (PWA 환경일 수 있음)", error);
  }
}

/**
 * 커스텀 이벤트 트래킹
 */
export async function trackEvent(
  name: AnalyticsEventName,
  params?: Record<string, string | number | boolean>
): Promise<void> {
  if (!isEnabled) {
    logger.debug(`[Analytics] 이벤트: ${name}`, params);
    return;
  }

  try {
    await FirebaseAnalytics.logEvent({ name, params: params ?? {} });
  } catch (error) {
    logger.warn(`이벤트 트래킹 실패: ${name}`, error);
  }
}

/**
 * 페이지뷰(화면) 트래킹
 */
export async function trackPageView(screenName: string): Promise<void> {
  if (!isEnabled) {
    logger.debug(`[Analytics] 페이지뷰: ${screenName}`);
    return;
  }

  try {
    await FirebaseAnalytics.setCurrentScreen({ screenName });
  } catch (error) {
    logger.warn(`페이지뷰 트래킹 실패: ${screenName}`, error);
  }
}

/**
 * Crashlytics 사용자 ID 설정
 */
export async function setCrashlyticsUser(userId: string): Promise<void> {
  if (!isEnabled) {
    logger.debug(`[Crashlytics] 사용자 설정: ${userId}`);
    return;
  }

  try {
    await FirebaseCrashlytics.setUserId({ userId });
  } catch (error) {
    logger.warn("Crashlytics 사용자 설정 실패", error);
  }
}

/**
 * Crashlytics 커스텀 로그
 */
export async function logCrashlytics(message: string): Promise<void> {
  if (!isEnabled) {
    logger.debug(`[Crashlytics] 로그: ${message}`);
    return;
  }

  try {
    await FirebaseCrashlytics.log({ message });
  } catch (error) {
    logger.warn("Crashlytics 로그 실패", error);
  }
}
